import imageCompression from 'browser-image-compression';
// Vite emite el UMD de la librería como un asset MÁS del bundle. Así viaja
// dentro del APK y del precaché del service worker — ver `COMPRESSION_LIB_URL`.
import libraryAssetUrl from 'browser-image-compression/dist/browser-image-compression.js?url';
import * as Sentry from '@sentry/react';

/**
 * Tope de captura de fotos.
 *
 * Decisión D1 del spec 2026-08-08. El número NO es libre: el visor a pantalla
 * completa (`ViewFulscreenImage`) y las descargas sirven el ORIGINAL, así que
 * comprimir en captura es irreversible. 2560 px reduce el volumen 4-5× y deja
 * margen para ampliar una matrícula o una fisura fina, que es para lo que se
 * abre el visor. El informe PDF ya recorta a 1024 px por su cuenta, así que
 * los informes no cambian.
 */
export const UPLOAD_MAX_DIMENSION = 2560;
export const UPLOAD_TARGET_MB = 1.2;

/**
 * Por debajo de esto no se toca el fichero: recodificar pierde calidad sin
 * ganar bytes apreciables.
 */
export const COMPRESSION_SKIP_BELOW_BYTES = 300 * 1024;

/**
 * URL del UMD de la librería, servida por la PROPIA app.
 *
 * 🔴 Sin esto la compresión depende de la cobertura. Con `useWebWorker: true`,
 * `browser-image-compression` arranca un worker desde un blob y dentro hace
 * `importScripts(options.libURL)`; si no se le pasa `libURL` usa por defecto
 * `https://cdn.jsdelivr.net/npm/browser-image-compression@2.0.2/...`. Es decir:
 * una app offline-first se bajaba la librería de un CDN público cada vez que un
 * operario hacía una foto en carretera.
 *
 * La librería tiene fallback al hilo principal si el worker RECHAZA, así que un
 * fallo limpio (avión, DNS caído) se recuperaba solo. Lo que NO cubre es la red
 * DEGRADADA, que es el patrón de este proyecto: `importScripts` es síncrono y no
 * tiene timeout, así que con la petición colgada la promesa no resuelve ni
 * rechaza y el `await compressForUpload(...)` se queda colgado para siempre. El
 * síntoma no serían fotos gordas sino el `setIsLoadingFiles(true)` de
 * `ActionEdition` sin su `false` — spinner eterno y fotos que no llegan a la
 * tira.
 *
 * Se resuelve a ABSOLUTA a propósito: `importScripts` resuelve relativo a la URL
 * `blob:` del worker, donde una ruta que empiece por `/` es ambigua.
 *
 * Se importa con `?url` en vez de copiar el fichero a `public/` para que no se
 * quede rancio: al subir de versión la dependencia, este asset sube con ella y
 * el worker nunca ejecuta una versión distinta de la que compila el bundle.
 */
export const COMPRESSION_LIB_URL = new URL(
  libraryAssetUrl,
  window.location.href,
).href;

/**
 * Techo de tiempo de la compresión.
 *
 * 🔴 Medido en tablet (Galaxy Tab Active5, WebView 151, 2026-09-16): un
 * `importScripts` contra un AGUJERO NEGRO de red —paquetes perdidos, que es la
 * red degradada de carretera, no el modo avión— seguía colgado **a los 60 s**
 * sin resolver ni rechazar. Con DNS muerto, en cambio, falla en 73 ms y la
 * librería se recupera sola: la diferencia entre rechazar y no contestar es
 * todo el defecto.
 *
 * Con `COMPRESSION_LIB_URL` apuntando al asset local ese camino ya no toca la
 * red, así que este techo no arregla nada que esté roto HOY. Está para cerrar
 * la clase de fallo: cualquier futuro que vuelva a meter una espera de red aquí
 * dentro degradaría a «foto sin comprimir», que es recuperable, en vez de a
 * «spinner que no termina», que no lo es.
 *
 * 20 s son ~17× lo que tarda una compresión real MEDIDA en la tablet: 1.146 ms
 * para una foto de 4,82 MB a 3264×2448 (salió a 1,03 MB / 2432×1824, un 79 %
 * menos). Margen de sobra para no abortar trabajo legítimo, incluso en frío.
 */
export const COMPRESSION_TIMEOUT_MS = 20_000;

/**
 * Rechaza si la promesa no termina a tiempo.
 *
 * No se cancela nada al vencer: la librería mata su worker al recibir mensaje o
 * error, y uno colgado en `importScripts` no responde a ninguno de los dos. Se
 * queda un worker huérfano por foto vencida — coste acotado y preferible a la
 * alternativa, que es no devolverle el control al operario.
 */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`compressForUpload: la compresión superó ${ms} ms`)),
      ms,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/**
 * ¿La foto ya cumple el tope de peso Y el de dimensión?
 *
 * Si es que sí, recomprimirla solo destruye calidad: una de 933 KB a 1600×1200
 * salía a 607 KB —perdiendo detalle a cambio de 300 KB— porque la librería
 * re-codifica siempre, aunque el fichero ya estuviera dentro del objetivo.
 * Antes no se notaba porque solo se comprimía por encima de 3-4 MB; al pasar a
 * comprimir siempre, esto se convirtió en una pérdida de calidad real. Cazado
 * midiendo con fotos ligeras de verdad, no con las de cámara.
 *
 * Decodificar para leer las dimensiones cuesta bastante menos que la
 * compresión completa, así que la comprobación se paga sola. Si el navegador
 * no puede decodificar (formato raro), se responde `false` y sigue el camino
 * normal: comprimir de más es preferible a no comprimir nada.
 */
async function yaCumpleElTope(file: File): Promise<boolean> {
  if (file.size > UPLOAD_TARGET_MB * 1024 * 1024) return false;

  try {
    const bitmap = await createImageBitmap(file);
    const cabe =
      bitmap.width <= UPLOAD_MAX_DIMENSION &&
      bitmap.height <= UPLOAD_MAX_DIMENSION;
    bitmap.close();
    return cabe;
  } catch {
    return false;
  }
}

/**
 * La copia de EXIF de la propia librería. Está en runtime
 * (`imageCompression.copyExifWithoutOrientation`) pero su `.d.ts` 2.0.2 no la
 * declara. Si una versión futura la quitara, esto lanza y `keepOriginalExif`
 * sube la foto sin EXIF — y el test contra la librería real
 * (`tests/unit/exif/preserve-exif-compat.spec.ts`, que importa ESTA función)
 * se pone en rojo.
 */
export const copyExifWithoutOrientation = (source: Blob, target: Blob): Promise<Blob> =>
  (
    imageCompression as unknown as {
      copyExifWithoutOrientation: (source: Blob, target: Blob) => Promise<Blob>;
    }
  ).copyExifWithoutOrientation(source, target);

/**
 * Copia el EXIF del original (fecha de captura, GPS) a la foto recomprimida.
 *
 * 🔴 Recodificar en un canvas BORRA el EXIF entero (spec 2026-09-23, fotos
 * geolocalizadas), y aquí importa de verdad: las fotos del recorrido pasan dos
 * veces por la compresión (cámara y `useSegmentPhotos`), y la cámara ya ha
 * escrito su EXIF en la primera.
 *
 * Se hace aquí, UNA vez, y no con la opción `preserveExif` de la librería: con
 * `useWebWorker` esa opción copia el bloque dentro del worker y otra vez al
 * volver al hilo principal, y el fichero sale con DOS APP1 Exif (medido en
 * Chromium). La función es la misma que usa la librería por dentro: copia el
 * APP1 tal cual y pone la orientación a 1, que es lo correcto porque el canvas
 * ya deja los píxeles derechos.
 *
 * Si la copia falla (p. ej. un EXIF con la orientación mal tipada, que la
 * librería rechaza), se sube la foto comprimida sin EXIF: es lo que pasaba
 * antes, y nunca se pierde la foto por los metadatos.
 */
async function keepOriginalExif(original: File, compressed: Blob): Promise<Blob> {
  const type = compressed.type || original.type;
  if (original.type !== 'image/jpeg' || type !== 'image/jpeg') return compressed;
  try {
    return await copyExifWithoutOrientation(original, compressed);
  } catch (err) {
    Sentry.captureException(err, { tags: { feature: 'camera', phase: 'keep-exif' } });
    return compressed;
  }
}

/**
 * Compresión ÚNICA de una foto antes de subirla.
 *
 * Sustituye a los tres bloques duplicados que había repartidos por la app
 * (cámara, galería y accidentes), cada uno con su umbral y ninguno con tope de
 * dimensión — el efecto era que una foto de 2,8 MB viajaba intacta.
 *
 * Dos cosas que NO hace, a propósito:
 *
 *  - **No se aplica al croquis.** Es un PNG de líneas sobre fondo claro y el
 *    JPEG con pérdida lo destroza. Los flujos de croquis no la llaman.
 *  - **No corrige la orientación EXIF a mano.** La librería ya lo hace (tiene
 *    entradas de changelog específicas para las fotos giradas de iPhone y
 *    Samsung). Hay test que lo cubre para que no se rompa en silencio al subir
 *    de versión.
 */
export async function compressForUpload(file: File): Promise<File> {
  if (file.size < COMPRESSION_SKIP_BELOW_BYTES) return file;
  if (await yaCumpleElTope(file)) return file;

  try {
    const blob = await withTimeout(
      imageCompression(file, {
        maxWidthOrHeight: UPLOAD_MAX_DIMENSION,
        maxSizeMB: UPLOAD_TARGET_MB,
        useWebWorker: true,
        // Obligatorio: sin él el worker se baja la librería de jsDelivr.
        libURL: COMPRESSION_LIB_URL,
      }),
      COMPRESSION_TIMEOUT_MS,
    );
    const withExif = await keepOriginalExif(file, blob);

    // Se conservan nombre y tipo del original: el nombre alimenta el ROID que
    // genera `generateImageNameWithROID` y el tipo se usa como Content-Type
    // del PUT prefirmado a Hetzner.
    return new File([withExif], file.name, {
      type: withExif.type || blob.type || file.type,
      lastModified: file.lastModified,
    });
  } catch (err) {
    // Nunca se pierde una foto por no poder comprimirla: se sube el original.
    Sentry.captureException(err);
    return file;
  }
}
